import logger from '../../utils/logger';
import Footer from '../Footer/Footer'; 
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { signOut } from '../../redux/slices/authSlice';
import { getCurrentUser, selectCurrentUser, selectUserInitialized } from '../../redux/slices/userSlice';
import { fetchFileExtensions, selectFileExtensionsInitialized } from '../../redux/slices/fileExtensionsSlice';
import { setSelectedDataProvider, selectSelectedDataProviderId } from '../../redux/slices/selectedDataProviderSlice';
import AppRouter from './../../AppRouter';
import GlobalNotifications from '../GlobalNotifications/GlobalNotifications';
import { setThemeMode } from "@gehc-eds/eds-lib";
import { EDSButtonGroup } from '@gehc-eds-react/eds-button-group';
import { EDSSystemNotification } from '@gehc-eds-react/eds-system-notification';
import { EDSDropdown } from '@gehc-eds-react/eds-dropdown';

const Header = () => {
      
    const [activeButton, setActiveButton] = useState("");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const currentPath = location.pathname;
    const [enableHomeIcon, setEnableHomeIcon] = useState(true);

    const currentUser = useSelector(selectCurrentUser);
    const isUserInitialized = useSelector(selectUserInitialized);
    const isFileExtensionsInitialized = useSelector(selectFileExtensionsInitialized);
    const selectedDataProviderId = useSelector(selectSelectedDataProviderId);
    const [dataProviderOptions, setDataProviderOptions] = useState([]);
    const showBanner = process.env.REACT_APP_SHOW_UAT_BANNER !== 'false';

    const activeUploads = useSelector((state) => state.uploadProgress?.activeUploads || []);
    const hasActiveUploads = activeUploads.some(
        upload => upload.status === 'in-progress' || 
                  upload.status === 'uploading' || 
                  upload.status === 'paused'
    );

    useEffect(() => {
        if (isUserInitialized && !isFileExtensionsInitialized) {
            dispatch(fetchFileExtensions());
        }
    }, [dispatch, isUserInitialized, isFileExtensionsInitialized]);

    const handleDataProviderSelect = (event) => {
        const selectedValue = event.detail?.value || event.target?.value;
        if (selectedValue && currentUser) {
            const selectedIndex = currentUser.DataProviderID.findIndex(id => id === selectedValue);
            if (selectedIndex !== -1) {
                const providerData = {
                    dataProviderId: selectedValue,
                    dataProviderName: currentUser.DataProviderName[selectedIndex],
                    dataProviderDisplayName: currentUser.DataProviderDisplayName[selectedIndex]
                };
                
                dispatch(setSelectedDataProvider(providerData));
                localStorage.setItem('selectedDataProvider', selectedValue);
            }
        }
    };

     useEffect(() => {
        if (currentUser && currentUser.DataProviderID) {
            const options = currentUser.DataProviderID.map((id, index) => ({
                value: id,
                label: currentUser.DataProviderDisplayName[index],
                disabled: false,
                selected: selectedDataProviderId === id // Use Redux state
            }));
            
            setDataProviderOptions(options);
            
            //  Restore persisted selection if no Redux state
            if (!selectedDataProviderId) {
                const persistedProvider = localStorage.getItem('selectedDataProvider');
                const providerToSelect = persistedProvider && options.find(opt => opt.value === persistedProvider) 
                    ? persistedProvider 
                    : options[0]?.value;
                
                if (providerToSelect) {
                    const selectedIndex = currentUser.DataProviderID.findIndex(id => id === providerToSelect);
                    if (selectedIndex !== -1) {
                        const providerData = {
                            dataProviderId: providerToSelect,
                            dataProviderName: currentUser.DataProviderName[selectedIndex],
                            dataProviderDisplayName: currentUser.DataProviderDisplayName[selectedIndex]
                        };
                        
                        dispatch(setSelectedDataProvider(providerData));
                    }
                }
            }
        }
    }, [currentUser, selectedDataProviderId, dispatch]);

     useEffect(() => {
        if (selectedDataProviderId) {
            setDataProviderOptions(prev => 
                prev.map(opt => ({
                    ...opt,
                    selected: opt.value === selectedDataProviderId
                }))
            );
        }
    }, [selectedDataProviderId]);
    
    const [buttonThemeOptions, setButtonThemeOptions] = useState([
        {"value":"1","label":"","icon":{"name":"ico-brightness-16"},"disabled":false,"selected":true},
        {"value":"2","label":"","icon":{"name":"ico-moon-16"},"disabled":false,"selected":false}
    ]);

    useEffect(() => {
        setThemeMode({theme: "enterprise", themeMode: "light"}); 
    }, [])

    const handleThemeChange = (e) =>{
        const selectedValue = e.target.value;
        if (selectedValue === '1') {
            setThemeMode({theme: "enterprise", themeMode: "light"}); 
        }else{
            setThemeMode({theme: "enterprise", themeMode: "dark"});
        }
        setButtonThemeOptions(prevOptions => 
            prevOptions.map(option => ({
                ...option,
                selected: option.value === selectedValue
            }))
        );
    }

    useEffect(() => {
        if (currentPath === '/' || currentPath === '/home') {
            setEnableHomeIcon(true);
            setActiveButton("landing");
        } 
        if (currentPath === '/data-upload') {
            setActiveButton("data-upload");
            setEnableHomeIcon(false);
        }
        if (currentPath !== '/' && currentPath !== '/home') {
            setEnableHomeIcon(false);
        }
        // if (currentPath === '/report') {
        //     setActiveButton("report");
        //     setEnableHomeIcon(false);
        // }
        
    }, [currentPath]);
    
    const handleNavigation = useCallback((event) => {
        const selectedValue = event.detail.value; 
        switch (selectedValue) {
            case 'landing':
                navigate("/home");
                break;  
            case 'data-upload':
                navigate("/data-upload");
                break;
            // case 'report':
            //     navigate("/report");
            //     break;
            default:
                break;
        }
            setActiveButton(selectedValue);
                   
    }, [navigate]);
    
    useEffect(() => {
        window.addEventListener('eds-masthead-side-option-select', handleNavigation);
        
        return () => {
            window.removeEventListener('eds-masthead-side-option-select', handleNavigation);
        };
    }, [handleNavigation]);
    
    const menu = { 
        "variant":"side-docked",
        "sideWidth":185,
        "options":[
            {
                label: "Home",
                value: "landing",
                isSelected: activeButton === "landing",
                icon: { name: "ico-home-24" },
                disabled : enableHomeIcon
            },
            {
                label: "Data Upload",
                value: "data-upload",
                isSelected: activeButton === "data-upload",
                icon: { name: "ico-upload-24" },
                disabled : false
            }
            // {
            //     label: "Report",
            //     value: "report",
            //     isSelected: activeButton === "report",
            //     icon: { name: "ico-report-24"},
            //     disabled : false
            // }
        ]
    };

    const handleLogout = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        logger.info('User initiated logout');
        await dispatch(signOut()).unwrap();
        logger.info('Logout completed successfully');
    }
    const handleProfileClick = () => {
        navigate("/profile");
    }
    useEffect(() => {
        // Set CSS variable based on banner visibility
        document.documentElement.style.setProperty(
            '--banner-height', 
            showBanner ? '32px' : '0px'
        );
    }, [showBanner]);
    return (
        <>
            {showBanner && (
            <div className="landing-page-notification-banner">
                <EDSSystemNotification
                    isOpen={true}
                    isInlineBanner={true}
                    variant="banner-information"
                    headline="This release is intended for User Acceptance Testing and feedback. Not for Research Use."
                    icon={{ name: "ico-info-16" }}
                    elevation={false}
                    squareCorners={"none"}
                    cornerRadius={"none"}        
                />
            </div>
        )}

        <eds-masthead 
            side-navigation-selector="#side-nav-container-3" 
            masthead-size="skinny" 
            product-name="Data Upload" 
            navigation-config={JSON.stringify(menu)}>
            <div slot="masthead-ribbon-slot" className="custom-class h-align--end display--flex w--full v-align-self--center">
                  {dataProviderOptions.length > 0 && (
                    <div className='u-mt---- u-mr--' style={{ minWidth: '200px', position: 'relative' }}>
                        <EDSDropdown
                            variant="standard"
                            name="data-provider-selector"
                            placeholder="Select Data Provider"
                            label={{
                                width: "",
                                position: "top",
                                text: ""
                            }}
                            instructionalText=""
                            validationError=""
                            width="100%"
                            options={dataProviderOptions}
                            value={selectedDataProviderId || ''}
                            multiSelect={{
                                enabled: false,
                                enableSelectAll: false,
                                displayType: "selected-label",
                                pinSelectedOptions: false
                            }}
                            disableClearSelection={true}
                            maxVisibleOptionsBeforeScroll={5}
                            filterText=""
                            noResultsText="No data providers found"
                            isDisabled={hasActiveUploads}
                            onEDSDropdownOptionSelect={handleDataProviderSelect}
                        />
                        {hasActiveUploads && (
                        <div className="data-provider-tooltip">
                            <eds-icon icon-name="ico-info-16"></eds-icon>
                            Cannot switch data provider while upload is in progress
                        </div>
                        )}
                    </div>
                  )}
                <div className='u-mt---- u-mr--'>
                    <EDSButtonGroup
                        name={"form-field"}
                        label={{"width":"","position":"side-left","text":""}}
                        instructionalText={""}                     
                        validationError={"Validation error"}
                        orientation={"horizontal"}
                        selectionType={"single"}
                        options={buttonThemeOptions}
                        onEDSButtonGroupClick = {handleThemeChange}
                    ></EDSButtonGroup>
                </div>
                <span onClick={handleProfileClick}><eds-icon icon-name="ico-user-24"  interactive-config='{ "paddingType": "small"}' className="menu-demo-button"></eds-icon></span>
                <span onClick={handleLogout}><eds-icon icon-name="ico-logout-24"  interactive-config='{ "paddingType": "small"}'></eds-icon></span>
            </div>       
        </eds-masthead>
        <div className="layout-container">
            <div id="side-nav-container-3" className="side-nav-container"></div>
            <div className="main-content">
                <GlobalNotifications />
                <AppRouter></AppRouter>
            </div>
        </div>
        
        <Footer releaseVersion={process.env.REACT_APP_VERSION} />
        </>
    );
}; 

export default Header;